import { App as AntdApp } from 'antd';
import { ModalFuncProps } from 'antd/lib/modal';
import { useCallback, useContext } from 'react';
import { ContextApp } from '../contexts/ContextApp';
import { Tools } from '../shared';
import { getRequestError, isRequestAborted } from '../shared/requestError';
import { T } from '../App';

function handleRequestCancellation(
  _context: string,
  originalError: unknown,
): boolean {
  return isRequestAborted(originalError);
}

export const useCustomMessage = (customT?: T) => {
  const {
    message: antdMessage,
    modal: antdModal,
    notification: antdNotification,
  } = AntdApp.useApp();
  const { t: contextT } = useContext(ContextApp);
  const t = customT ?? contextT;

  const MESSAGE_CREATE_SUCCESS = t('abm.message.createSuccess');
  const MESSAGE_CREATE_ERROR = t('abm.message.createError');
  const MESSAGE_UPDATE_SUCCESS = t('abm.message.updateSuccess');
  const MESSAGE_UPDATE_ERROR = t('abm.message.updateError');
  const MESSAGE_DELETE_SUCCESS = t('abm.message.deleteSuccess');
  const MESSAGE_DELETE_ERROR = t('abm.message.deleteError');

  const DEFAULT_TIMEOUT_LOADING = 150000;
  const DEFAULT_TIMEOUT_MESSAGE = 1200;
  const DEFAULT_ERROR_MESSAGE = t('abm.message.defaultError');

  // const { Logger } = LogService();

  /**
   * Iniciar un loading
   * @param op
   */
  const messageLoading = (op: {
    context: string;
    message?: string;
    time?: number;
  }) => {
    antdMessage.destroy();
    antdMessage.loading(
      op.message || Tools.capitalize(t('action.loading')),
      op.time || DEFAULT_TIMEOUT_LOADING,
    );
    // if (op?.message && typeof op.message === 'string') {
    //   Logger.info({
    //     context: op.context,
    //     message: op.message,
    //   });
    // }
  };

  const messageDestroy = (time?: number) => {
    setTimeout(() => {
      antdMessage.destroy();
    }, time || DEFAULT_TIMEOUT_MESSAGE);
  };

  const messageModalSuccess = (op: {
    context: string;
    message: string;
    props?: ModalFuncProps;
  }) => {
    antdMessage.destroy();
    antdModal.success(
      op?.props || {
        content: op.message,
      },
    );
    // if (typeof op.message === 'string') {
    //   Logger.info(op);
    // }
  };

  const messageModalError = (op: {
    context: string;
    message?: string;
    error?: unknown;
    err?: Error;
    props?: ModalFuncProps;
  }) => {
    const error = op.error ?? op.err;
    if (handleRequestCancellation(op.context, error)) return;
    antdMessage.destroy();
    antdModal.error(
      op?.props || {
        content: op.message ?? getErrorMessage(error),
      },
    );
    // if (op.err) {
    //   Logger.error({
    //     context: op.context,
    //     message: op.err.message,
    //   });
    // } else if (typeof op.message === 'string') {
    //   Logger.warn({
    //     context: op.context,
    //     message: op.message,
    //   });
    // }
  };

  const messageModalInfo = (op: {
    context: string;
    message: string;
    props?: ModalFuncProps;
  }) => {
    antdMessage.destroy();
    antdModal.info(
      op?.props || {
        content: op.message,
      },
    );
    // if (typeof op.message === 'string') {
    //   Logger.info(op);
    // }
  };

  const messageModalConfirm = (op: {
    context: string;
    message?: string;
    props: ModalFuncProps;
  }) => {
    antdModal.confirm({
      ...op.props,
      content: op.props.content ?? op.message,
    });
  };

  const messageModalWarning = (op: {
    context: string;
    message: string;
    props?: ModalFuncProps;
  }) => {
    antdModal.warning(
      op.props || {
        content: op.message,
      },
    );
  };

  const notificationInfo = (op: {
    context: string;
    props: Parameters<typeof antdNotification.info>[0];
  }) => antdNotification.info(op.props);

  const notificationWarning = (op: {
    context: string;
    props: Parameters<typeof antdNotification.warning>[0];
  }) => antdNotification.warning(op.props);

  const notificationError = (op: {
    context: string;
    error?: unknown;
    props: Parameters<typeof antdNotification.error>[0];
  }) => {
    if (handleRequestCancellation(op.context, op.error)) return;
    return antdNotification.error(op.props);
  };

  /**
   * Resultado de un proceso de exito
   * @param op
   */
  const messageSuccess = (op: {
    context: string;
    message: string;
    time?: number;
  }) => {
    antdMessage.destroy();
    antdMessage.success(op.message);
    messageDestroy(op.time);
    // Logger.info(op);
  };

  /**
   * Resultado de un proceso que dio error
   * @param err
   */
  const messageError = (op: {
    context: string;
    message?: string;
    error?: unknown;
    time?: number;
  }) => {
    if (handleRequestCancellation(op.context, op.error)) return;
    antdMessage.destroy();
    antdMessage.error(op.message ?? getErrorMessage(op.error));
    messageDestroy(op.time);
    // Logger.warn(op);
  };

  /**
   * Para acciones de ABM
   */
  const messageCreating = (op: { context: string; message: string }) => {
    messageLoading({
      context: op.context,
      message: t('abm.message.creating', { replace: { entity: op.message } }),
    });
  };
  const messageUpdating = (op: { context: string; message: string }) => {
    messageLoading({
      context: op.context,
      message: t('abm.message.updating', { replace: { entity: op.message } }),
    });
  };
  const messageDeleting = (op: { context: string; message: string }) => {
    messageLoading({
      context: op.context,
      message: t('abm.message.deleting', { replace: { entity: op.message } }),
    });
  };

  /**
   * Para resultados de ABM
   */
  const messageCreateSuccess = (op: { context: string }) => {
    messageSuccess({ context: op.context, message: MESSAGE_CREATE_SUCCESS });
  };
  const messageCreateError = (
    op: { context: string; error?: unknown },
    originalError: unknown = op.error,
  ) => {
    const error = getRequestError(originalError);
    // El complemento detallado del error solo se aplica al alta; extenderlo requiere aprobación.
    messageError({
      context: op.context,
      error: originalError,
      message:
        MESSAGE_CREATE_ERROR +
        (error?.status_code
          ? ` ${Tools.capitalize(t('entity.reason'))} ${getErrorMessage(error)}`
          : ''),
    });
  };
  const messageUpdateSuccess = (op: { context: string }) => {
    messageSuccess({ context: op.context, message: MESSAGE_UPDATE_SUCCESS });
  };
  const messageUpdateError = (op: { context: string; error?: unknown }) => {
    messageError({ ...op, message: MESSAGE_UPDATE_ERROR });
  };
  const messageDeleteSuccess = (op: { context: string }) => {
    messageSuccess({ context: op.context, message: MESSAGE_DELETE_SUCCESS });
  };
  const messageDeleteError = (op: { context: string; error?: unknown }) => {
    messageError({ ...op, message: MESSAGE_DELETE_ERROR });
  };

  const getErrorMessage = useCallback(
    (originalError: unknown): string => {
      const error = getRequestError(originalError);
      if (
        error.message_translation_key &&
        error.message_translation_key !== 'error.backend.generalErrorMessage'
      ) {
        let translatedText = '';
        if (
          error.message_translation_key.includes('{{') &&
          !error.message_translation_key.includes('undefined') &&
          !error.message_translation_key.includes('{{}}')
        ) {
          translatedText = error.message_translation_key.replaceAll(
            /{{([^{}]+)}}/g,
            (_: string, key: string) => {
              return t(key as never);
            },
          );
        } else {
          translatedText = t(error.message_translation_key as never, {
            defaultValue: error.message,
          });
        }

        return translatedText || error.message || DEFAULT_ERROR_MESSAGE;
      }
      return error.message || DEFAULT_ERROR_MESSAGE;
    },
    [t],
  );

  const showMessageError = useCallback(
    (op: { context: string; error: unknown }) => {
      const { context } = op;
      const error = getRequestError(op.error);
      const { status_code } = error;
      if (status_code && status_code !== 1 && status_code !== '1') {
        messageModalError({
          context: context,
          error: op.error,
          message: getErrorMessage(error),
        });
      } else {
        messageError({
          context: context,
          error: op.error,
          message: Tools.capitalize(t('entity.generalError')),
        });
      }
    },
    [getErrorMessage],
  );

  return {
    messageDestroy,
    messageLoading,
    messageSuccess,
    messageError,
    messageModalSuccess,
    messageModalError,
    messageModalInfo,
    messageModalConfirm,
    messageModalWarning,
    notificationInfo,
    notificationWarning,
    notificationError,
    messageCreating,
    messageUpdating,
    messageDeleting,
    messageCreateSuccess,
    messageCreateError,
    messageUpdateSuccess,
    messageUpdateError,
    messageDeleteSuccess,
    messageDeleteError,
    getErrorMessage,
    showMessageError,
    DEFAULT_ERROR_MESSAGE,
    t,
    // Logger,
  };
};
